/**
 * Build the canonical gallery share URL that the branded export QR encodes.
 *
 * This matches the link produced by the existing "Copy link" action in the
 * gallery (`/gallery/:id`), so a scanned QR opens the same public gallery a
 * shared link would. Each layout (including a forked copy) has its own id, so
 * each fork's QR resolves to its own gallery.
 *
 * SECURITY / CORRECTNESS:
 *  - Returns null when no persisted id is available. Callers MUST treat null as
 *    "not saved yet" and hide the QR — never render a QR to a dead/wrong target.
 *  - `version` is appended as a query param only when provided; the gallery
 *    ignores unknown params, so this is safe even if version deep-linking is
 *    not yet honored by the route.
 *
 * @param {Object} args
 * @param {string} [args.origin] - Defaults to window.location.origin.
 * @param {string|number|null|undefined} args.id - Persisted layout id.
 * @param {string|number|null|undefined} [args.version] - Current version.
 * @returns {string|null}
 */
export function buildGalleryShareUrl({ origin, id, version } = {}) {
  const resolvedOrigin =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : "");

  if (!resolvedOrigin) return null;

  const trimmedId =
    id === 0 || id ? String(id).trim() : "";
  if (!trimmedId) return null;

  const base = `${resolvedOrigin}/gallery/${encodeURIComponent(trimmedId)}`;

  if (version === 0 || version) {
    return `${base}?version=${encodeURIComponent(String(version))}`;
  }

  return base;
}

export default buildGalleryShareUrl;
